'use client';

import React, { useState } from 'react';
import { Search, Calendar, AlertTriangle, Download, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';

const incidentTypes = [
  { id: 'itype-all', label: 'All Types' },
  { id: 'itype-slip', label: 'Slip / Trip / Fall' },
  { id: 'itype-chemical', label: 'Chemical Exposure' },
  { id: 'itype-electrical', label: 'Electrical' },
  { id: 'itype-struck', label: 'Struck By / Against' },
  { id: 'itype-ergonomic', label: 'Ergonomic / Strain' },
  { id: 'itype-fire', label: 'Fire / Explosion' },
  { id: 'itype-vehicle', label: 'Vehicle / Mobile Equipment' },
  { id: 'itype-confined', label: 'Confined Space' },
];

const severities = [
  { id: 'sev-all', label: 'All Severities' },
  { id: 'sev-critical', label: 'Critical' },
  { id: 'sev-high', label: 'High' },
  { id: 'sev-medium', label: 'Medium' },
  { id: 'sev-low', label: 'Low' },
];

const recordability = [
  { id: 'rec-all', label: 'All' },
  { id: 'rec-yes', label: 'OSHA Recordable' },
  { id: 'rec-no', label: 'Non-Recordable' },
];

export default function IncidentFilters() {
  const [search, setSearch] = useState('');
  const [incidentType, setIncidentType] = useState('itype-all');
  const [severity, setSeverity] = useState('sev-all');
  const [recordable, setRecordable] = useState('rec-all');
  const [showExport, setShowExport] = useState(false);

  const handleExport = (format: 'csv' | 'pdf') => {
    // TODO: Backend integration — POST /api/ehs/incidents/export
    toast.success(`Exporting incident report as ${format.toUpperCase()}`, {
      description: `Filtered by: ${incidentType} · ${severity}`,
    });
    setShowExport(false);
  };

  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="flex items-center gap-2 bg-[hsl(var(--muted))] rounded-lg px-3 py-2 min-w-[200px] flex-1 max-w-xs">
          <Search size={13} className="text-[hsl(var(--muted-foreground))] shrink-0" />
          <input
            type="text"
            placeholder="Search by ID, location, reporter..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] outline-none flex-1"
          />
        </div>

        {/* Incident Type */}
        <div className="flex items-center gap-1.5 bg-[hsl(var(--muted))] rounded-lg px-3 py-2">
          <AlertTriangle size={12} className="text-[hsl(var(--muted-foreground))]" />
          <select
            value={incidentType}
            onChange={(e) => setIncidentType(e.target.value)}
            className="bg-transparent text-sm font-500 text-[hsl(var(--foreground))] outline-none cursor-pointer"
          >
            {incidentTypes.map((t) => (
              <option key={t.id} value={t.id} className="bg-[hsl(var(--card))]">{t.label}</option>
            ))}
          </select>
        </div>

        {/* Severity */}
        <div className="flex items-center gap-1.5 bg-[hsl(var(--muted))] rounded-lg px-3 py-2">
          <SlidersHorizontal size={12} className="text-[hsl(var(--muted-foreground))]" />
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="bg-transparent text-sm font-500 text-[hsl(var(--foreground))] outline-none cursor-pointer"
          >
            {severities.map((s) => (
              <option key={s.id} value={s.id} className="bg-[hsl(var(--card))]">{s.label}</option>
            ))}
          </select>
        </div>

        {/* Recordability */}
        <div className="flex items-center gap-1.5 bg-[hsl(var(--muted))] rounded-lg px-3 py-2">
          <Calendar size={12} className="text-[hsl(var(--muted-foreground))]" />
          <select
            value={recordable}
            onChange={(e) => setRecordable(e.target.value)}
            className="bg-transparent text-sm font-500 text-[hsl(var(--foreground))] outline-none cursor-pointer"
          >
            {recordability.map((r) => (
              <option key={r.id} value={r.id} className="bg-[hsl(var(--card))]">{r.label}</option>
            ))}
          </select>
        </div>

        {/* Export */}
        <div className="relative ml-auto">
          <button
            onClick={() => setShowExport((v) => !v)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-600 hover:opacity-90 transition-all duration-150 active:scale-95"
          >
            <Download size={13} />
            Export
          </button>
          {showExport && (
            <div className="absolute right-0 top-full mt-1 w-40 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg shadow-lg z-20 overflow-hidden animate-fade-in">
              <button onClick={() => handleExport('csv')} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[hsl(var(--muted))] transition-colors">
                Export as CSV
              </button>
              <button onClick={() => handleExport('pdf')} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[hsl(var(--muted))] transition-colors">
                Export as PDF
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}